import React from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import unifiedTheme from './theme/unifiedTheme';
import ViewportScaler from './theme/viewportScaling';
import Register from './components/Auth/Register';
import PreRegister from './components/Auth/PreRegister';
import Login from './components/Auth/Login';
import GoogleCallback from './components/Auth/GoogleCallback';
import Dashboard from './components/Dashboard/Dashboard';

import WeddingDetailsDashboard from './components/WeddingDetailsDashboard';
import WeddingBudget from './components/WeddingBudget';
import WeddingVows from './components/WeddingVows';
import SpotifyCallback from './components/SpotifyCallback';
import WeddingPartyRoles from './components/WeddingPartyRoles';
import CoupleProfileEditor from './components/PartnerDetails/CoupleProfileEditor';
import InvitationGenerator from './components/InvitationGenerator';
import StationaryGifts from './components/StationaryGifts/StationaryGifts';
import Invitations from './components/StationaryGifts/Invitations';
import Gifts from './components/StationaryGifts/Gifts';
import Orders from './components/StationaryGifts/Orders';
import GuestList from './components/GuestList/GuestList';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography
} from '@mui/material';
import Timeline from './components/Timeline/Timeline';
import ResponsiveLayout from './components/layout/ResponsiveLayout';
import TodoList from './components/TodoList/TodoList';
import Settings from './components/Settings/Settings';
import Profile from './components/Profile/Profile';
import Billing from './components/Billing/Billing';
import SupplierRegister from './components/Supplier/SupplierRegister';
import SupplierPreRegister from './components/Supplier/SupplierPreRegister';
import SupplierRegistrationFlow from './components/Supplier/Registration/SupplierRegistrationFlow';
import SupplierDashboard from './components/Supplier/SupplierDashboard';
import SupplierLogin from './components/Supplier/SupplierLogin';
import SupplierLayout from './components/Supplier/SupplierLayout';
import BusinessVerification from './components/Supplier/BusinessVerification';
import SupplierFeatureGate from './components/Business/SupplierFeatureGate';
import SupplierSettings from './components/Supplier/SupplierSettings';
import SupplierAppsPage from './components/Supplier/Apps/SupplierAppsPage';
import MyCouplesDashboard from './components/Supplier/MyCouples/MyCouplesDashboard';
import LeadDetailView from './components/Supplier/MyCouples/LeadDetailView';
import CRMAnalyticsPage from './components/Supplier/MyCouples/CRMAnalyticsPage';
import SupplierSearch from './components/SupplierSearch/SupplierSearch';
import CoupleSuppliersPage from './components/Supplier/CoupleSuppliersPage';
import ChatContainer from './components/Chat/ChatContainer';
import MessagesArea from './components/Chat/MessagesArea';
import AIChatTestPage from './components/AIChat/AIChatTestPage';
import LumiChatPage from './components/Lumi/LumiChatPage';
import AutomationsPage from './components/Automations/AutomationsPage';
import SupplierProfileEdit from './components/Supplier/SupplierProfileEdit';
import FormLeadsManagement from './components/Supplier/FormLeadsManagement';
import CouplesWedding from './components/Supplier/CouplesWedding';
import WeddingDetailsMasterPage from './components/Supplier/WeddingDetails/WeddingDetailsMasterPage';
import InvoiceList from './components/Supplier/Invoices/InvoiceList';
import InvoiceForm from './components/Supplier/Invoices/InvoiceForm';
import InvoiceDetail from './components/Supplier/Invoices/InvoiceDetail';
import ProductList from './components/Supplier/Products/ProductList';
import ProductForm from './components/Supplier/Products/ProductForm';
import ProposalList from './components/Supplier/Proposals/ProposalList';
import ProposalForm from './components/Supplier/Proposals/ProposalForm';
import ProposalDetail from './components/Supplier/Proposals/ProposalDetail';
import TemplateForm from './components/Supplier/Proposals/TemplateForm';
import ProposalSupplierPreviewPage from './components/Supplier/Proposals/ProposalSupplierPreviewPage';
import TemplateList from './components/Supplier/Proposals/TemplateList';
import StripeConnectSetup from './components/Supplier/Settings/StripeConnectSetup';
import InvoiceSettings from './components/Supplier/Settings/InvoiceSettings';
import Share from './components/Share/Share';
import { AuthProvider } from './context/AuthContext';
import { PlatformAlertsProvider } from './context/PlatformAlertsContext';
import { SocketProvider } from './context/SocketContext';
import { BusinessProvider, useBusiness } from './context/BusinessContext';
import { NotificationProvider } from './context/NotificationContext';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import NoWeddingAccess from './components/NoWeddingAccess';
import ProtectedRoute from './components/PrivateRoute';
import PermissionProtectedRoute from './components/Auth/ProtectedRoute';
import SeatingPlan from './components/SeatingPlan/SeatingPlan';
import { useAuth } from './context/AuthContext';
import SupplierAgreement from './components/Legal/SupplierAgreement';
import RefundPolicy from './components/Legal/RefundPolicy';
import CancellationPolicy from './components/Legal/CancellationPolicy';
import ServiceLevelDisclaimer from './components/Legal/ServiceLevelDisclaimer';
import ReviewModerationPolicy from './components/Legal/ReviewModerationPolicy';
import PublicLegalDocument from './components/Legal/PublicLegalDocument';
import DeleteAccountPage from './components/Legal/DeleteAccountPage';
import CreateWedding from './components/CreateWedding';
import ViewSupplierProfile from './components/Supplier/ViewSupplierProfile';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';

// Admin Components
import AdminLayout from './components/Admin/AdminLayout';
import AdminDashboard from './components/Admin/Dashboard';
import AdminUsers from './components/Admin/Users';
import Suppliers from './components/Admin/Suppliers';
import AdminLogin from './components/Admin/AdminLogin';
import AdminVerification from './components/Admin/AdminVerification';
import PlatformUpdates from './components/Admin/PlatformUpdates';
import LegalDocuments from './components/Admin/LegalDocuments';
import Referrals from './components/Admin/Referrals';
import Commissions from './components/Admin/Commissions';
import ReferralConfigs from './components/Admin/ReferralConfigs';
import SystemSettings from './components/Admin/SystemSettings';
import ReportedMessages from './components/Admin/ReportedMessages';
import SecurityIssues from './components/Admin/SecurityIssues';
import PlatformStatusCheck from './components/PlatformStatusCheck';

import MobileSupplierMenu from './components/Supplier/MobileSupplierMenu';
import MobileGuestMenu from './components/GuestList/MobileGuestMenu';
import StylesExample from './components/StylesExample';
// Unified Booking System Components
import UnifiedBookingDashboard from './components/UnifiedBooking/UnifiedBookingDashboard';
import ServiceTypeManagement from './components/UnifiedBooking/ServiceTypeManagement';
import UnifiedCalendar from './components/UnifiedBooking/UnifiedCalendar';
import BookingForm from './components/UnifiedBooking/BookingForm';
import SupplierManualBookingForm from './components/UnifiedBooking/SupplierManualBookingForm';
import SupplierProfileBooking from './components/UnifiedBooking/SupplierProfileBooking';
import BookingDetail from './components/UnifiedBooking/BookingDetail';
import BookingSettings from './components/UnifiedBooking/BookingSettings';
import CouplesBookings from './components/UnifiedBooking/CouplesBookings';
import TeamMemberRegistration from './components/Auth/TeamMemberRegistration';
import NotificationsPage from './components/Notifications/NotificationsPage';
import BusinessSubscriptions from './components/Business/BusinessSubscriptions';
import BusinessBilling from './components/Business/BusinessBilling';
import People from './components/Business/People';
import StaffMemberDetail from './components/StaffManagement/StaffMemberDetail';
import StaffSchedulePage from './components/Schedules/StaffSchedulePage';
import Schedules from './components/Schedules/Schedules';
import PublicBusinessProfile from './components/Business/PublicBusinessProfile';
import ProposalViewPage from './components/Proposal/ProposalViewPage';
import PublicQuestionnairePage from './components/Forms/PublicQuestionnairePage';
import PublicEmbedBookPage from './components/UnifiedBooking/PublicEmbedBookPage';
import MyDocumentsPage from './components/Documents/MyDocumentsPage';


// Wrapper component to pass businessId to BookingForm
function BookingFormWrapper() {
  const { supplierId } = useParams();
  const { selectedBusiness } = useBusiness();

  // If we have a supplierId from the URL, use that as the businessId
  // Otherwise, use the selected business from context
  const businessId = supplierId || selectedBusiness?.business._id;

  return <BookingForm businessId={businessId} />;
}

const LEGACY_COUPLE_SECTION_ALIASES = {
  dashboard: 'couple-information',
  basic: 'couple-information',
  'basic-information': 'couple-information',
  'communication-preferences': 'couple-information',
  'wedding-role': 'guest-list',
  'dietary-accessibility': 'guest-list',
  cultural: 'cultural',
  'cultural-religious': 'cultural',
  'photography-privacy': 'profile',
  personal: 'personal',
  'personal-touches': 'personal',
  extras: 'extras',
  'optional-extras': 'extras',
};

function getLegacyRedirectPath(sectionId) {
  const mapped = LEGACY_COUPLE_SECTION_ALIASES[sectionId] || 'couple-information';
  if (mapped === 'profile') return '/profile';
  if (mapped === 'guest-list') return '/guest-list';
  return `/wedding-details?section=${mapped}`;
}

function LegacyPartnerDetailsRedirect() {
  const { sectionId } = useParams();
  return <Navigate to={getLegacyRedirectPath(sectionId)} replace />;
}

function LegacyCoupleDetailsRedirect() {
  const { sectionId } = useParams();
  return <Navigate to={getLegacyRedirectPath(sectionId)} replace />;
}

/** Old emailed links used /proposal/:token — canonical path is /agreement/:token */
function LegacyPublicProposalToAgreement() {
  const { token } = useParams();
  return <Navigate to={`/agreement/${token}`} replace />;
}

/** Old supplier URLs used /supplier/proposals/... — canonical path is /supplier/agreements/... */
function LegacySupplierProposalsToAgreements() {
  const params = useParams();
  const { search } = useLocation();
  const splat = params['*'] ?? '';
  const suffix = splat ? `/${splat}` : '';
  return <Navigate to={`/supplier/agreements${suffix}${search}`} replace />;
}

// Root redirect component - redirects based on user type or to /no-access
function RootRedirect() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const userType = localStorage.getItem('userType');
  const token = localStorage.getItem('token');
  const loginPath = userType === 'supplier'
    ? '/supplier/login'
    : userType === 'admin'
      ? '/admin/login'
      : '/login';

  // Check if WordPress proxy is active - if so, don't redirect
  // WordPress proxy routes should handle their own routing
  const isWordPressProxy = typeof window !== 'undefined' && 
    window.__WORDPRESS_PROXY__ === true && 
    !!window.__PROFILE_BUSINESS_ID__;
  
  if (isWordPressProxy) {
    return null; // Let WordPress proxy route handle it
  }

  // Wait for auth to initialize
  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Checking your session...
        </Typography>
      </Box>
    );
  }

  // If not logged in (or stale token state), fail closed to login.
  if (!token) {
    return <Navigate to={loginPath} state={location.state} replace />;
  }

  if (!user) {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('weddingId');
    return <Navigate to={loginPath} state={location.state} replace />;
  }

  // Redirect based on user type
  if (userType === 'supplier') {
    return <Navigate to="/supplier/dashboard" replace />;
  }
  
  if (userType === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Default to regular dashboard for couples/regular users
  return <Navigate to="/dashboard" replace />;
}

function PlatformAnnouncementPopup() {
  const { user, unseenAnnouncement, dismissAnnouncement } = useAuth();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setOpen(Boolean(user && unseenAnnouncement));
  }, [user, unseenAnnouncement]);

  const handleClose = async () => {
    if (unseenAnnouncement?.id) {
      await dismissAnnouncement(unseenAnnouncement.id);
    }
    setOpen(false);
  };

  if (!user || !unseenAnnouncement) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2, px: 4 }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 600,
            mb: unseenAnnouncement.imageUrl ? 3 : 2,
            px: 1
          }}
        >
          {unseenAnnouncement.title || 'Platform update'}
        </Typography>
        {unseenAnnouncement.imageUrl && (
          <Box
            component="img"
            src={unseenAnnouncement.imageUrl}
            alt=""
            sx={{
              maxWidth: '100%',
              maxHeight: 280,
              objectFit: 'contain',
              borderRadius: 1,
              mb: 3,
              mx: 'auto',
              display: 'block'
            }}
          />
        )}
        <Typography variant="body1" sx={{ mb: 2, px: 1 }}>
          {unseenAnnouncement.body}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button onClick={handleClose} variant="contained">
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function App() {
  return (
    <ThemeProvider theme={unifiedTheme}>
      <CssBaseline />
      <ViewportScaler>
        <ErrorBoundary>
          <PlatformAlertsProvider>
          <AuthProvider>
            <SocketProvider>
              <NotificationProvider>
              <Box
                sx={{
                  minHeight: '100vh',
                  width: '100%',
                  maxWidth: '100vw',
                  overflowX: 'hidden',
                  backgroundImage: `url(${process.env.PUBLIC_URL}/img/brand-bg.jpg)`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundAttachment: 'fixed',
                  backgroundRepeat: 'no-repeat',
                  position: 'relative',
                }}
              >
                <PWAInstallPrompt />
                <PlatformAnnouncementPopup />
                <Routes>
                {/* Root route - redirect based on auth status */}
                <Route path="/" element={<RootRedirect />} />
                
                {/* Public routes */}
                <Route path="/login" element={
                  <PlatformStatusCheck>
                    <Login />
                  </PlatformStatusCheck>
                } />
                <Route path="/couple/login" element={
                  <PlatformStatusCheck>
                    <Login skipRoleSelection />
                  </PlatformStatusCheck>
                } />
                <Route path="/register" element={
                  <PlatformStatusCheck>
                    <Box sx={{ 
                      minHeight: '100vh', 
                      bgcolor: 'secondary.main'
                    }}>
                      <Register />
                    </Box>
                  </PlatformStatusCheck>
                } />
                <Route path="/couple/register" element={
                  <PlatformStatusCheck>
                    <Box sx={{ 
                      minHeight: '100vh', 
                      bgcolor: 'secondary.main'
                    }}>
                      <Register />
                    </Box>
                  </PlatformStatusCheck>
                } />
                <Route path="/preregister" element={
                  <Box sx={{ 
                    minHeight: '100vh', 
                    bgcolor: 'secondary.main'
                  }}>
                    <PreRegister />
                  </Box>
                } />
                <Route path="/auth/google/callback" element={<GoogleCallback />} />
                <Route path="/spotify-callback" element={<SpotifyCallback />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/team-member-register" element={<TeamMemberRegistration />} />
                <Route path="/proposal/:token" element={<LegacyPublicProposalToAgreement />} />
                <Route path="/agreement/:token" element={<ProposalViewPage />} />
                <Route path="/questionnaire/:token" element={<PublicQuestionnairePage />} />
                <Route path="/book/:token" element={<PublicEmbedBookPage />} />
                {/* Legal Documents - Terms, Privacy, Cookies open in modal; others remain as pages */}
                <Route path="/privacy-policy" element={<PublicLegalDocument title="Privacy Policy" fileName="privacy-policy" userType="both" />} />
                <Route path="/cookie-policy" element={<PublicLegalDocument title="Cookies Policy" fileName="cookie-policy" userType="both" />} />
                <Route path="/cookies-policy" element={<Navigate to="/cookie-policy" replace />} />
                <Route path="/business-terms" element={<PublicLegalDocument title="Business Terms and Conditions" fileName="business-terms" userType="supplier" />} />
                <Route path="/general-terms-couples" element={<PublicLegalDocument title="General Terms and Conditions" fileName="general-terms-couples" userType="user" />} />
                <Route path="/supplier-agreement" element={<SupplierAgreement />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                <Route path="/service-level-disclaimer" element={<ServiceLevelDisclaimer />} />
                <Route path="/review-moderation-policy" element={<ReviewModerationPolicy />} />
                <Route path="/delete-account" element={<DeleteAccountPage />} />

                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute requireWedding={false}>
                      <AdminLayout>
                        <Routes>
                          <Route path="dashboard" element={<AdminDashboard />} />
                          <Route path="users" element={<AdminUsers />} />
                          <Route path="suppliers" element={<Suppliers />} />
                          <Route path="suppliers/:supplierId/profile" element={<ViewSupplierProfile />} />
                          <Route path="verification" element={<AdminVerification />} />
                          <Route path="platform-updates" element={<PlatformUpdates />} />
                          <Route path="legal-documents" element={<LegalDocuments />} />
                          <Route path="referrals" element={<Referrals />} />
                          <Route path="commissions" element={<Commissions />} />
                          <Route path="referral-configs" element={<ReferralConfigs />} />
                          <Route path="system-settings" element={<SystemSettings />} />
                          <Route path="reported-messages" element={<ReportedMessages />} />
                          <Route path="security-issues" element={<SecurityIssues />} />
                          <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                        </Routes>
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Supplier routes - don't require wedding access */}
                <Route path="/supplier/login" element={
                  <PlatformStatusCheck>
                    <SupplierLogin />
                  </PlatformStatusCheck>
                } />
                <Route path="/supplier/register" element={
                  <PlatformStatusCheck>
                    <SupplierRegistrationFlow />
                  </PlatformStatusCheck>
                } />
                <Route path="/supplier/register-old" element={<SupplierRegister />} />
                <Route path="/supplier/preregister" element={
                  <Box sx={{ 
                    minHeight: '100vh', 
                    bgcolor: 'secondary.main'
                  }}>
                    <SupplierPreRegister />
                  </Box>
                } />
                
                {/* Public Business Profile Routes - These are PUBLIC routes that don't require authentication */}
                {/* WordPress proxy check: if WordPress proxy is active, let the catch-all route handle it */}
                {/* If WordPress proxy is NOT active, render PublicBusinessProfile directly */}
                <Route 
                  path="/profile/:businessId/:slug" 
                  element={
                    (() => {
                      // If WordPress proxy is active, return null so catch-all route handles it
                      if (typeof window !== 'undefined' && window.__WORDPRESS_PROXY__ === true) {
                        return null;
                      }
                      // Otherwise, render the public profile (no authentication required)
                      return <PublicBusinessProfile />;
                    })()
                  } 
                />
                <Route 
                  path="/profile/:businessId" 
                  element={
                    (() => {
                      // If WordPress proxy is active, return null so catch-all route handles it
                      if (typeof window !== 'undefined' && window.__WORDPRESS_PROXY__ === true) {
                        return null;
                      }
                      // Otherwise, render the public profile (no authentication required)
                      return <PublicBusinessProfile />;
                    })()
                  } 
                />
                <Route
                  path="/supplier/*"
                  element={
                    <ProtectedRoute requireWedding={false}>
                      <BusinessProvider>
                        <SupplierLayout>
                          <Routes>
                            <Route path="/dashboard" element={<SupplierDashboard />} />
                            <Route path="/verification/:businessId" element={<BusinessVerification />} />
                            <Route path="/profile" element={<Navigate to="/supplier/edit-profile" replace />} />
                            <Route path="/availability" element={<Navigate to="/supplier/bookings" replace />} />
                            <Route path="/bookings" element={<UnifiedBookingDashboard />} />
                            <Route path="/bookings/calendar" element={<SupplierFeatureGate feature="calendar_sync" reason="Calendar Sync"><UnifiedCalendar userType="supplier" /></SupplierFeatureGate>} />
                            <Route path="/bookings/services" element={<ServiceTypeManagement />} />
                            <Route path="/bookings/settings" element={<BookingSettings />} />
                            <Route path="/bookings/new" element={<SupplierManualBookingForm />} />
                            <Route path="/bookings/:id" element={<BookingDetail />} />
                            <Route path="/settings" element={<SupplierSettings />} />
                            <Route path="/billing" element={<PermissionProtectedRoute permission="view_financials"><BusinessBilling /></PermissionProtectedRoute>} />
                            <Route path="/subscriptions" element={<PermissionProtectedRoute permission="view_financials"><BusinessSubscriptions /></PermissionProtectedRoute>} />
                            <Route path="/people" element={<SupplierFeatureGate feature="team_management" reason="Team Management"><People /></SupplierFeatureGate>} />
                            <Route path="/notifications" element={<NotificationsPage />} />
                            <Route path="/messages" element={<PermissionProtectedRoute permission="view_messages"><MessagesArea showSidebar={true} /></PermissionProtectedRoute>} />
                            <Route path="/messages/:conversationId" element={<PermissionProtectedRoute permission="view_messages"><MessagesArea showSidebar={true} /></PermissionProtectedRoute>} />
                            <Route path="/crm" element={<PermissionProtectedRoute permission="view_bookings"><MyCouplesDashboard /></PermissionProtectedRoute>} />
                            <Route path="/crm/analytics" element={<CRMAnalyticsPage />} />
                            <Route path="/crm/leads/:leadId" element={<LeadDetailView />} />
                            <Route path="/wedding/:weddingId/details" element={<WeddingDetailsMasterPage />} />
                            <Route path="/forms/submissions" element={<PermissionProtectedRoute permission="view_bookings"><FormLeadsManagement formsView="submissions" /></PermissionProtectedRoute>} />
                            <Route path="/forms/builder" element={<PermissionProtectedRoute permission="manage_bookings"><FormLeadsManagement formsView="builder" /></PermissionProtectedRoute>} />
                            <Route path="/forms/create" element={<PermissionProtectedRoute permission="manage_bookings"><FormLeadsManagement formAction="create" /></PermissionProtectedRoute>} />
                            <Route path="/invoices" element={<PermissionProtectedRoute permission="view_financials"><SupplierFeatureGate feature="invoice_payments" reason="Invoices and payments"><InvoiceList /></SupplierFeatureGate></PermissionProtectedRoute>} />
                            <Route path="/invoices/new" element={<PermissionProtectedRoute permission="view_financials"><SupplierFeatureGate feature="invoice_payments" reason="Invoices and payments"><InvoiceForm /></SupplierFeatureGate></PermissionProtectedRoute>} />
                            <Route path="/invoices/:id" element={<PermissionProtectedRoute permission="view_financials"><SupplierFeatureGate feature="invoice_payments" reason="Invoices and payments"><InvoiceDetail /></SupplierFeatureGate></PermissionProtectedRoute>} />
                            <Route path="/invoices/:id/edit" element={<PermissionProtectedRoute permission="view_financials"><SupplierFeatureGate feature="invoice_payments" reason="Invoices and payments"><InvoiceForm /></SupplierFeatureGate></PermissionProtectedRoute>} />
                            <Route path="/invoices/settings" element={<PermissionProtectedRoute permission="view_financials"><SupplierFeatureGate feature="invoice_payments" reason="Invoices and payments"><InvoiceSettings /></SupplierFeatureGate></PermissionProtectedRoute>} />
                            <Route path="/products" element={<PermissionProtectedRoute permission="view_bookings"><ProductList /></PermissionProtectedRoute>} />
                            <Route path="/products/new" element={<PermissionProtectedRoute permission="view_bookings"><ProductForm /></PermissionProtectedRoute>} />
                            <Route path="/products/:id/edit" element={<PermissionProtectedRoute permission="view_bookings"><ProductForm /></PermissionProtectedRoute>} />
                            <Route path="/agreements" element={<PermissionProtectedRoute permission="view_bookings"><ProposalList /></PermissionProtectedRoute>} />
                            <Route path="/agreements/new" element={<PermissionProtectedRoute permission="view_bookings"><ProposalForm /></PermissionProtectedRoute>} />
                            <Route path="/agreements/templates" element={<PermissionProtectedRoute permission="view_bookings"><TemplateList /></PermissionProtectedRoute>} />
                            <Route path="/agreements/templates/new" element={<PermissionProtectedRoute permission="view_bookings"><TemplateForm /></PermissionProtectedRoute>} />
                            <Route path="/agreements/templates/:id/edit" element={<PermissionProtectedRoute permission="view_bookings"><TemplateForm /></PermissionProtectedRoute>} />
                            <Route path="/agreements/preview" element={<PermissionProtectedRoute permission="view_bookings"><ProposalSupplierPreviewPage /></PermissionProtectedRoute>} />
                            <Route path="/agreements/:id" element={<PermissionProtectedRoute permission="view_bookings"><ProposalDetail /></PermissionProtectedRoute>} />
                            <Route path="/agreements/:id/edit" element={<PermissionProtectedRoute permission="view_bookings"><ProposalForm /></PermissionProtectedRoute>} />
                            <Route path="/proposals/*" element={<LegacySupplierProposalsToAgreements />} />
                            <Route path="/settings/stripe-connect" element={<StripeConnectSetup />} />
                            <Route path="/team-members" element={<Navigate to="/supplier/schedules" replace />} />
                            <Route path="/staff" element={<Navigate to="/supplier/schedules" replace />} />
                            <Route path="/schedules" element={<PermissionProtectedRoute permission="manage_availability"><SupplierFeatureGate feature="staff_scheduler" reason="Staff schedules"><Schedules /></SupplierFeatureGate></PermissionProtectedRoute>} />
                            <Route path="/schedules/staff/:staffMemberId" element={<PermissionProtectedRoute permission="manage_availability"><StaffSchedulePage /></PermissionProtectedRoute>} />
                            <Route path="/staff/:memberId" element={<StaffMemberDetail />} />
                            <Route path="/forms/edit/:formId" element={<PermissionProtectedRoute permission="manage_bookings"><FormLeadsManagement formAction="edit" /></PermissionProtectedRoute>} />
                            <Route path="/forms" element={<Navigate to="/supplier/forms/submissions" replace />} />
                            <Route path="/weddings" element={<CouplesWedding />} />
                            <Route path="/edit-profile" element={<SupplierProfileEdit />} />
                            <Route path="/lumi" element={<LumiChatPage />} />
                            <Route
                              path="/apps"
                              element={
                                <SupplierFeatureGate
                                  feature={['messaging', 'calendar_sync']}
                                  requireAll={false}
                                  reason="App integrations, shared inbox, and calendar sync"
                                >
                                  <SupplierAppsPage />
                                </SupplierFeatureGate>
                              }
                            />
                            <Route path="/automations" element={<AutomationsPage />} />
                            <Route path="/chat" element={<Navigate to="/supplier/messages" replace />} />
                            <Route path="/chat/:conversationId" element={<Navigate to="/supplier/messages/:conversationId" replace />} />
                            <Route path="/ai-chat-test" element={<AIChatTestPage />} />
                            <Route path="*" element={<Navigate to="/supplier/dashboard" replace />} />
                          </Routes>
                        </SupplierLayout>
                      </BusinessProvider>
                    </ProtectedRoute>
                  }
                />

                {/* No wedding access route - minimal protection to handle expired sessions */}
                <Route 
                  path="/no-access" 
                  element={
                    <ProtectedRoute requireWedding={false}>
                      <NoWeddingAccess />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Create wedding route */}
                <Route 
                  path="/create-wedding" 
                  element={
                    <ProtectedRoute requireWedding={false}>
                      <CreateWedding />
                    </ProtectedRoute>
                  } 
                />

                {/* Protected routes that require wedding access */}
                {/* WordPress URLs are handled inside ProtectedRoute component */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute requireWedding={true}>
                      <ResponsiveLayout>
                        <Routes>
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/lumi" element={<LumiChatPage />} />
                          <Route path="/automations" element={<AutomationsPage />} />
                          <Route path="/chat" element={<ChatContainer showSidebar={true} chatFilter="suppliers" />} />
                          <Route path="/chat/:conversationId" element={<ChatContainer showSidebar={true} chatFilter="suppliers" />} />
                          <Route path="/ai-chat-test" element={<AIChatTestPage />} />
                          <Route path="/invitation-generator" element={<InvitationGenerator />} />
                          <Route path="/stationary-gifts" element={<StationaryGifts />} />
                          <Route path="/stationary/invitations" element={<Invitations />} />
                          <Route path="/stationary-gifts/gifts" element={<Gifts />} />
                          <Route path="/stationary-gifts/orders" element={<Orders />} />
                          <Route path="/guest-list" element={<GuestList />} />
                          <Route path="/seating-plan" element={<SeatingPlan />} />
                          <Route path="/timeline" element={<Timeline />} />
                          <Route path="/todo-list" element={<TodoList />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/notifications" element={<NotificationsPage />} />
                          <Route path="/profile" element={<Profile />} />
                          <Route path="/billing" element={<Billing />} />
                          <Route path="/wedding-details" element={<WeddingDetailsDashboard />} />
                          <Route path="/budget" element={<WeddingBudget />} />
                          <Route path="/vows" element={<WeddingVows />} />
                          <Route path="/wedding" element={<Navigate to="/wedding-details" replace />} />
                          <Route path="/wedding/details" element={<Navigate to="/wedding-details" replace />} />
                          <Route path="/wedding/overview" element={<Navigate to="/wedding-details" replace />} />
                          <Route path="/account/wedding-details" element={<Navigate to="/wedding-details" replace />} />
                          <Route path="/partner-details" element={<Navigate to="/wedding-details?section=couple-information" replace />} />
                          <Route path="/partner-details/:sectionId" element={<LegacyPartnerDetailsRedirect />} />
                          <Route path="/couple-details/basic-information" element={<Navigate to="/wedding-details?section=couple-information" replace />} />
                          <Route path="/couple-details/communication-preferences" element={<Navigate to="/wedding-details?section=couple-information" replace />} />
                          <Route path="/couple-details/wedding-role" element={<Navigate to="/guest-list" replace />} />
                          <Route path="/couple-details/dietary-accessibility" element={<Navigate to="/guest-list" replace />} />
                          <Route path="/couple-details/cultural-religious" element={<Navigate to="/wedding-details?section=cultural" replace />} />
                          <Route path="/couple-details/photography-privacy" element={<Navigate to="/profile" replace />} />
                          <Route path="/couple-details/personal-touches" element={<Navigate to="/wedding-details?section=personal" replace />} />
                          <Route path="/couple-details/optional-extras" element={<Navigate to="/wedding-details?section=extras" replace />} />
                          <Route path="/couple-details" element={<Navigate to="/wedding-details?section=couple-information" replace />} />
                          <Route path="/couple-details/:sectionId" element={<LegacyCoupleDetailsRedirect />} />
                          <Route path="/couple" element={<Navigate to="/wedding-details?section=couple-information" replace />} />
                          <Route path="/couple/:sectionId" element={<LegacyCoupleDetailsRedirect />} />
                          <Route path="/couple-profile/photography" element={<Navigate to="/profile" replace />} />
                          <Route path="/couple-profile" element={<Navigate to="/wedding-details?section=couple-information" replace />} />
                          <Route path="/couple-profile/:sectionId" element={<CoupleProfileEditor />} />
                          <Route path="/wedding/budget" element={<Navigate to="/budget" replace />} />
                          <Route path="/wedding/style-theme" element={<Navigate to="/wedding-details?section=style-theme" replace />} />
                          <Route path="/wedding/venues" element={<Navigate to="/wedding-details?section=venues" replace />} />
                          <Route path="/wedding/vows" element={<Navigate to="/vows" replace />} />
                          <Route path="/wedding/music" element={<Navigate to="/wedding-details?section=music" replace />} />
                          <Route path="/guests/wedding-party" element={<WeddingPartyRoles />} />
                          <Route path="/wedding/party-roles" element={<Navigate to="/guests/wedding-party" replace />} />
                          
                          {/* Mobile menu routes */}
                          <Route path="/mobile/suppliers" element={<MobileSupplierMenu />} />
                          <Route path="/mobile/guests" element={<MobileGuestMenu />} />
                          
                          {/* Supplier routes */}
                          <Route path="/supplier-search" element={<SupplierSearch />} />
                          <Route path="/suppliers" element={<CoupleSuppliersPage />} />
                          <Route path="/suppliers/my-suppliers" element={<Navigate to="/suppliers" replace />} />
                          <Route path="/suppliers/shortlisted" element={<Navigate to="/suppliers" replace />} />
                          <Route path="/dashboard/supplier/:supplierId" element={<ViewSupplierProfile />} />
                          <Route path="/dashboard/supplier/:supplierId/book" element={<SupplierProfileBooking />} />
                          <Route path="/book-supplier/:supplierId" element={
                            <BusinessProvider>
                              <BookingFormWrapper />
                            </BusinessProvider>
                          } />
                          <Route path="/bookings" element={<CouplesBookings />} />
                          <Route path="/bookings/:id" element={<BookingDetail />} />
                          <Route path="/documents" element={<MyDocumentsPage />} />
                          <Route path="/invoice/:id" element={<InvoiceDetail readOnly />} />
                          <Route path="/share" element={<Share />} />
                          
                          {/* Redirect old supplier profile route to new one */}
                          <Route path="/supplier/:supplierId" element={<Navigate to={location => `/dashboard/supplier/${location.pathname.split('/').pop()}`} replace />} />
                          
                          {/* Style reference route */}
                          <Route path="/styles" element={
                            <ProtectedRoute requireWedding={false}>
                              <StylesExample />
                            </ProtectedRoute>
                          } />
                          
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </ResponsiveLayout>
                    </ProtectedRoute>
                  }
                />

              </Routes>
              </Box>
              </NotificationProvider>
            </SocketProvider>
          </AuthProvider>
          </PlatformAlertsProvider>
        </ErrorBoundary>
      </ViewportScaler>
    </ThemeProvider>
  );
}

export default App; 